'use client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CustomSelect } from 'src/components/design-system/form/CustomSelect'
import { NewsItem } from 'src/components/items/NewsItem'
import { Pagination } from 'src/components/list/Pagination'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { newsService } from 'src/_services/client/news.service'
import LINKS from 'src/_helpers/links'
import { NEWS_PER_PAGE } from 'src/_helpers/constants'
import { buildCurrentStateUrl } from 'src/_helpers/buildCurrentStateUrl'
import { readListQueryPage } from 'src/_helpers/listQueryParams'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { getLocalizedThumb } from 'src/_helpers/getLocalizedThumb'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { getNewsLinkLocale, getNewsTranslation } from 'src/components/news/newsTranslation'

const formatDate = (rawDate) => {
  if (!rawDate) return null

  const date = new Date(rawDate)
  if (!Number.isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  if (typeof rawDate === 'string') {
    const normalized = rawDate.replace(/\//g, '.').trim()
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(normalized)) {
      return normalized
    }
  }

  return typeof rawDate === 'string' ? rawDate : null
}

const buildColumnBorders = (index) => {
  const classes = []

  if (index % 2 === 1) {
    classes.push('t:border-l', 't:border-dark-blue-dark-blue5', 't:pl-20')
  }

  if (index % 4 !== 0) {
    classes.push('d:border-l', 'd:border-dark-blue-dark-blue5', 'd:pl-20')
  }

  return classes.join(' ')
}

async function resolveTagsForItems(items = []) {
  const uuids = new Set()

  items.forEach((item) => {
    const parsed = Array.isArray(item?.tagsUuids)
      ? item.tagsUuids
      : typeof item?.tagsUuids === 'string'
        ? item.tagsUuids.split(',').map((uuid) => uuid.trim())
        : []
    parsed.filter(Boolean).forEach((uuid) => uuids.add(uuid))
  })

  if (!uuids.size) {
    return {}
  }

  const tags = await newsService.getTagsByUuids(Array.from(uuids))
  const tagsByUuid = {}

  tags.forEach((tag) => {
    const key = tag?.uuid || tag?.id || tag?.slug
    if (key) {
      tagsByUuid[key] = tag
    }
  })

  return tagsByUuid
}

async function fetchNews(page, sort, setPages, setCurrentNews, locale, journalUuid, publisherUuid) {
  try {
    const updatedNews = journalUuid
      ? await newsService.getJournalPage(journalUuid, page, NEWS_PER_PAGE, sort?.value)
      : publisherUuid
        ? await newsService.getPublisherPage(publisherUuid, page, NEWS_PER_PAGE, sort?.value)
        : await newsService.getPage(page, NEWS_PER_PAGE, sort?.value)
    const orderQuery = sort?.value ? `order=${sort.value}` : undefined
    const queryParams = buildCurrentStateUrl('', page, undefined, orderQuery, locale)
    window.history.replaceState(null, '', queryParams)

    const pages = Math.ceil(updatedNews['hydra:totalItems'] / NEWS_PER_PAGE)
    const tagsByUuid = await resolveTagsForItems(updatedNews['hydra:member'])
    const enrichedItems = updatedNews['hydra:member'].map((item) => {
      const parsed = Array.isArray(item?.tagsUuids)
        ? item.tagsUuids
        : typeof item?.tagsUuids === 'string'
          ? item.tagsUuids.split(',').map((uuid) => uuid.trim())
          : []
      const resolvedTags = parsed
        .filter(Boolean)
        .map((uuid) => tagsByUuid[uuid])
        .filter(Boolean)
      return resolvedTags.length ? { ...item, tags: resolvedTags } : item
    })

    setPages(pages)
    setCurrentNews(enrichedItems)
  } catch (error) {
    console.error('Error fetching news:', error)
  }
}

// Z `journalUuid` lista pokazuje aktualności czasopisma, a detal otwiera się w jego kontekście (`journalSlugs`:
// slug czasopisma w każdym języku). Na domenie czasopisma albo wydawcy adres detalu daje `detailRoute` (mapa tras
// domeny z `:slug`), a lista wydawcy pobiera kolejne strony z `publisherUuid`. Aktualność bez tytułu w języku
// strony pokazuje treść w innym języku i prowadzi do detalu w języku z getNewsLinkLocale (`linkLocales`: języki
// strony; `anySlugLocale`: detal znajdzie slug z innego języka).
export default function CollectionClient({
  searchParams,
  locale,
  news,
  initialOrder,
  journalUuid = null,
  journalSlugs = null,
  publisherUuid = null,
  detailRoute = null,
  linkLocales = null,
  anySlugLocale = false,
}) {
  const translateEntry = useTranslateEntry()
  const normalizedInitialOrder = (initialOrder || searchParams.order) === 'asc' ? 'asc' : 'desc'

  const sortOptions = useMemo(
    () => [
      { value: 'desc', label: translateEntry('T_SORT_FROM_NEWEST') },
      { value: 'asc', label: translateEntry('T_SORT_FROM_OLDEST') },
    ],
    [translateEntry],
  )

  const initialSortOption = sortOptions.find((option) => option.value === normalizedInitialOrder) || sortOptions[0]

  const [sort, setSort] = useState(initialSortOption)
  const [page, setPage] = useState(readListQueryPage(searchParams, locale))
  const [currentNews, setCurrentNews] = useState(news['hydra:member'])
  const [pages, setPages] = useState(Math.ceil(news['hydra:totalItems'] / NEWS_PER_PAGE))

  const handleSetSort = useCallback(
    (option) => {
      const nextValue = option?.target?.value ?? option?.value ?? option
      const nextOption = sortOptions.find((opt) => opt.value === nextValue)
      if (!nextOption) return
      setSort(nextOption)
      setPage(1)
    },
    [sortOptions],
  )

  const handleChangePage = (newPage) => {
    setPage(newPage)
  }

  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    fetchNews(page, sort, setPages, setCurrentNews, locale, journalUuid, publisherUuid)
  }, [page, sort])

  return (
    <div className="flex flex-col gap-24">
      <CustomSelect
        name="sort"
        label={translateEntry('T_SORT')}
        options={sortOptions}
        defaultValue={sort?.value}
        color="white"
        onChange={handleSetSort}
        classNameContainer="max-w-[310px] mt-24 z-11"
      />

      {/* Kolumny z borderem są szersze o border + padding (21px), żeby treść, a więc i miniatury
          w kadrze z CMS, miały we wszystkich kolumnach tę samą szerokość i wysokość */}
      <div className="list-elements grid gap-21 border-t border-dark-blue-dark-blue4 pb-64 pt-16 t:grid-cols-[calc((100%_-_42px)/2)_calc((100%_-_42px)/2_+_21px)] d:grid-cols-[calc((100%_-_126px)/4)_repeat(3,calc((100%_-_126px)/4_+_21px))]">
        {currentNews?.map((el, index) => {
          const { translation, contentLocale } = getNewsTranslation(el, locale)
          const title = translation?.title
          const description = translation?.lead
          const date = formatDate(el?.publishedAt || el?.createdAt)
          const tags = Array.isArray(el?.tags)
            ? el.tags
                .map((tag) => {
                  const label = tag?.translations?.[locale]?.title
                  if (!label) return null
                  const normalized = label.toLowerCase()
                  const type = normalized.includes('nowość') || normalized.includes('nowosc') ? 'new' : 'default'
                  return { label, type }
                })
                .filter(Boolean)
            : []
          const thumb = getLocalizedThumb(el, 'list', locale)
          const thumbUrl = thumb ? getMediaUrl(thumb.url, thumb.version) : null
          const alt = translation?.alt || title
          const linkLocale = getNewsLinkLocale(contentLocale, locale, { linkLocales, anySlugLocale })
          const url = !linkLocale
            ? null
            : detailRoute
              ? buildLocalizedPath(detailRoute, linkLocale, { slug: translation?.slug })
              : journalSlugs
                ? buildLocalizedPath(LINKS.JOURNALS.NEWS_DETAIL, linkLocale, {
                    journal: journalSlugs[linkLocale],
                    slug: translation?.slug,
                  })
                : buildLocalizedPath(LINKS.NEWS.DETAIL, linkLocale, { slug: translation?.slug })
          const columnBorders = buildColumnBorders(index)

          return (
            <NewsItem
              key={title || url || index}
              date={date}
              title={title}
              desc={description}
              url={url}
              src={thumbUrl}
              alt={alt}
              showImagePlaceholder
              tags={tags}
              classCustom={`border-b border-dark-blue-dark-blue4 pb-16 ${columnBorders}`}
            />
          )
        })}
      </div>

      {pages > 1 && <Pagination className="mb-64 mx-auto" page={page} pages={pages} setPage={handleChangePage} />}
    </div>
  )
}
